import { useEffect, useState } from "react";
import { PackagePlus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function StockAdmin() {
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/companies")
      .then((r) => setCompanies(r.data || []))
      .catch(() => toast.error("Unable to load companies"));
  }, []);

  useEffect(() => {
    setProductId("");
    if (!companyId) {
      setProducts([]);
      return;
    }

    api.get("/admin/products", { params: { company_id: companyId } })
      .then((r) => setProducts(r.data || []))
      .catch(() => toast.error("Unable to load products"));
  }, [companyId]);

  const selectedProduct = products.find((p) => p._id === productId);

  const addStock = async () => {
    const qty = Number(quantity);

    if (!companyId) return toast.error("Select a company");
    if (!productId) return toast.error("Select a product");
    if (!Number.isFinite(qty) || qty <= 0) {
      return toast.error("Enter a valid quantity");
    }

    try {
      setBusy(true);

      await api.post(`/admin/products/${productId}/stock-receipt`, {
        quantity_mt: qty,
      });

      toast.success("Stock added successfully");
      setQuantity("");

      const r = await api.get("/admin/products", {
        params: { company_id: companyId },
      });
      setProducts(r.data || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        "Unable to add stock"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-100 text-emerald-700">
            <PackagePlus size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Add Stock
            </h1>
            <p className="text-sm text-slate-500">
              Stock management
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-5">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Company
          </label>
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 bg-white"
          >
            <option value="">Select company</option>
            {companies.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Product
          </label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            disabled={!companyId}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 bg-white disabled:bg-slate-100"
          >
            <option value="">
              {companyId ? "Select product" : "Select company first"}
            </option>
            {products.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {selectedProduct && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
            <div className="text-sm text-slate-500">Current Stock</div>
            <div className="text-2xl font-bold text-slate-900">
              {Number(selectedProduct.stock_mt || 0).toFixed(3)} MT
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Quantity to Add (MT)
          </label>
          <input
            type="number"
            min="0.001"
            step="0.001"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="Enter quantity"
            className="w-full rounded-xl border border-slate-300 px-4 py-3"
          />
        </div>

        <button
          type="button"
          onClick={addStock}
          disabled={busy}
          className="w-full rounded-xl bg-emerald-600 text-white font-semibold py-3 disabled:opacity-50"
        >
          {busy ? "Adding Stock..." : "Add Stock"}
        </button>
      </div>
    </div>
  );
}
